import { useEffect, useMemo, useState } from "react";
import { Button, Card, Space, Typography } from "antd";
import {
  IdcardOutlined,
  PauseCircleOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from "@ant-design/icons";
import OliveebProductAds from "./OliveebProductAds";
import "./PromotionRail.css";

const { Paragraph, Text, Title } = Typography;

const ICONS = {
  profile: IdcardOutlined,
  backgroundCheck: SafetyCertificateOutlined,
  hiring: SolutionOutlined,
  rocket: RocketOutlined,
};

const DEFAULT_ITEMS = [
  {
    eyebrow: "Profile strength",
    title: "Stand out before you apply",
    description:
      "Complete your verified Olreadia profile so employers can trust your experience faster.",
    actionLabel: "Improve profile",
    featured: true,
    icon: "profile",
  },
  {
    eyebrow: "Hiring made clearer",
    title: "Manage every hiring step",
    titleLevel: 5,
    description:
      "Keep applications, interviews, and decisions organized inside one workspace.",
    icon: "hiring",
  },
  {
    eyebrow: "Sponsored",
    slotLabel: "Ad space",
    placeholder: true,
    icon: "rocket",
  },
];

const PromotionCard = ({ item }) => {
  if (item.slotLabel || item.placeholder) {
    return (
      <OliveebProductAds
        className="promotion-rail-ads-section"
        compact
        intervalMs={item.intervalMs || 4200}
        showHeader
      />
    );
  }

  const Icon = ICONS[item.icon] || PictureOutlined;
  const ActionIcon = item.actionIcon ? ICONS[item.actionIcon] || ICONS[item.icon] || PictureOutlined : Icon;
  const className = [
    "promotion-card",
    item.featured ? "promotion-card--featured" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card className={className}>
      {item.eyebrow ? <Text className="promotion-card__eyebrow">{item.eyebrow}</Text> : null}
      <div className="promotion-card__content-row">
        <span className="promotion-card__icon">
          <Icon />
        </span>
        <div className="promotion-card__copy">
          {item.title ? <Title level={item.titleLevel || 4}>{item.title}</Title> : null}
          {item.description ? <Paragraph>{item.description}</Paragraph> : null}
        </div>
        {item.actionLabel ? (
          <Button type="primary" icon={<ActionIcon />} onClick={item.onAction}>
            {Array.isArray(item.actionLabelLines) && item.actionLabelLines.length ? (
              <span className="promotion-card__button-label">
                {item.actionLabelLines.map((line) => <span key={line}>{line}</span>)}
              </span>
            ) : item.actionLabel}
          </Button>
        ) : null}
      </div>
    </Card>
  );
};

const PromotionRail = ({
  items = DEFAULT_ITEMS,
  className = "",
  ariaLabel = "Promotions",
  rotating = false,
  intervalMs = 5000,
  visibleCount = 3,
  showControls = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const safeItems = items?.length ? items : DEFAULT_ITEMS;
  const pinnedItems = useMemo(() => safeItems.filter((item) => item.pinned), [safeItems]);
  const rotatingItems = useMemo(() => safeItems.filter((item) => !item.pinned), [safeItems]);
  const rotatingVisibleCount = Math.max((visibleCount || safeItems.length) - pinnedItems.length, 0);
  const shouldRotate = rotating && rotatingItems.length > rotatingVisibleCount && rotatingVisibleCount > 0;
  const paused = hoverPaused || userPaused;
  const visibleItems = useMemo(() => {
    if (!shouldRotate) return [...pinnedItems, ...rotatingItems].slice(0, visibleCount || safeItems.length);
    return [
      ...pinnedItems,
      ...Array.from({ length: rotatingVisibleCount }, (_, offset) => rotatingItems[(activeIndex + offset) % rotatingItems.length]),
    ];
  }, [activeIndex, pinnedItems, rotatingItems, rotatingVisibleCount, safeItems.length, shouldRotate, visibleCount]);
  const visiblePinnedItems = visibleItems.filter((item) => item.pinned);
  const visibleRotatingItems = visibleItems.filter((item) => !item.pinned);
  const controlsVisible = showControls && shouldRotate;

  const showPrevious = () => {
    if (!shouldRotate) return;
    setActiveIndex((current) => (current - 1 + rotatingItems.length) % rotatingItems.length);
  };

  const showNext = () => {
    if (!shouldRotate) return;
    setActiveIndex((current) => (current + 1) % rotatingItems.length);
  };

  useEffect(() => {
    if (!shouldRotate || paused) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % rotatingItems.length);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, paused, rotatingItems.length, shouldRotate]);

  useEffect(() => {
    setActiveIndex(0);
  }, [safeItems]);

  return (
    <aside
      className={[
        "promotion-rail",
        shouldRotate ? "promotion-rail--rotating" : "",
        className,
      ].filter(Boolean).join(" ")}
      aria-label={ariaLabel}
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
    >
      <Space direction="vertical" size={14} className="promotion-rail__stack">
        {visiblePinnedItems.map((item, index) => (
          <PromotionCard key={`pinned-${item.id || item.title || item.eyebrow || index}`} item={item} />
        ))}
        {controlsVisible ? (
          <div className="promotion-rail-player" aria-label="Promotion message controls">
            <Button
              shape="circle"
              size="small"
              icon={<StepBackwardOutlined />}
              onClick={showPrevious}
              aria-label="Previous promotion messages"
            />
            <Button
              type="primary"
              size="small"
              icon={userPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={() => setUserPaused((current) => !current)}
              className="promotion-rail-player__toggle"
            >
              {userPaused ? "Play" : "Pause"}
            </Button>
            <Button
              shape="circle"
              size="small"
              icon={<StepForwardOutlined />}
              onClick={showNext}
              aria-label="Next promotion messages"
            />
          </div>
        ) : null}
        {visibleRotatingItems.map((item, index) => (
          <PromotionCard key={`${activeIndex}-${item.id || item.title || item.eyebrow || index}`} item={item} />
        ))}
      </Space>
    </aside>
  );
};

export default PromotionRail;
